import Code from '@/components/dashboard/Code';
import ClientCode from '@/components/dashboard/ClientCode';
import SectionActions from '@/docs/SectionActions';
import Links from '@/docs/ExampleLinks';
import { useRef } from 'react'
import SEOMetadata from '@/components/SEOMetadata';
import DocHeading from '@/docs/DocHeading';

export default function InsidersAPIDoc() {
  const sectionRef = useRef(null);
  return (
    <div className="api-documentation space-y-8 lg:max-w-[calc(100vw-340px)] md:max-w-[calc(100vw-80px)] max-w-[calc(100vw-30px)] overflow-hidden md:overflow-visible pb-50">
      <SEOMetadata title="Insiders & Ownership API" description="The Insiders & Ownership API provides detailed information on institutional and fund ownership,
          individual insider holdings, and recent insider transactions for publicly traded companies...."  canonicalUrl="https://axionquant.com/docs/03-fundamentals/insiders/activity" />
      <div className="mb-10">
        <DocHeading className="text-3xl font-bold text-gray-900 mb-4">Insiders & Ownership API</DocHeading>
        <p className="text-lg text-gray-700 max-w-[900px]">
          The Insiders & Ownership API provides detailed information on institutional and fund ownership,
          individual insider holdings, and recent insider transactions for publicly traded companies.
          Data is sourced from SEC filings (Forms 3, 4, 5, 13F, and 13D/G) and provides transparency
          into who owns and trades company shares.
        </p>
      </div>

      <div className="space-y-12" ref={sectionRef}>


        {/* ========== 5. Net Share Purchase Activity ========== */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /insiders/:ticker/activity
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Provides a 6-month summary of insider buying and selling activity.
                Includes counts of buy/sell transactions, total shares bought and sold,
                and net activity. Useful for gauging insider sentiment.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Path Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">ticker</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm"><span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">Required</span></td>
                      <td className="px-4 py-3 text-sm text-gray-700">Stock ticker symbol</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <Links links={['/insiders/AAPL/activity', '/insiders/MSFT/activity']} />

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Key Response Fields</h3>
              <div className="space-y-3">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">period</p>
                  <p className="text-sm text-gray-600">Time window of the summary (always &quot;6m&quot; for six months)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">buyInfoCount, sellInfoCount</p>
                  <p className="text-sm text-gray-600">Number of buy and sell transactions</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">buyInfoShares, sellInfoShares</p>
                  <p className="text-sm text-gray-600">Total shares bought and sold</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">buyPercentInsiderShares, sellPercentInsiderShares</p>
                  <p className="text-sm text-gray-600">Percentage of total insider shares represented by buys/sells</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">netInfoCount</p>
                  <p className="text-sm text-gray-600">Net number of transactions (buys minus sells)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">netInfoShares</p>
                  <p className="text-sm text-gray-600">Net shares acquired (positive = net buying)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">netPercentInsiderShares</p>
                  <p className="text-sm text-gray-600">Net shares as a percentage of total insider holdings</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">totalInsiderShares</p>
                  <p className="text-sm text-gray-600">Total shares held by insiders</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Net Share Purchase Activity (Apple)" contentRef={sectionRef} />

              <h3 className="text-sm font-semibold mb-3">Net Share Purchase Activity (Apple)</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="insiders/ticker/activity"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response</p>
                <Code id="03-fundamentals/insiders/activity"></Code>
              </div>
            </div>
          </div>
        </div>


      </div>
    </div>
  );
}
